<template>
  <div class="history-section">
    <div class="text-h2">
      Job Profile Acknowledgement History
    </div>
    <EmployeeAcknowledgementHistoryEmptyState
      v-if="!acknowledgements.length"
    />
    <div
      v-else
      class="history-table-wrap"
    >
      <table class="history-table text-body-2">
        <thead>
          <tr>
            <th class="text-left">
              <span class="history-th-content">
                Job Profile
                <InsightsTooltip
                  :tooltip="sliderHeaderProfileTooltip"
                  tooltip-direction="end"
                  class="ml-1"
                  @click.stop
                />
              </span>
            </th>
            <th class="text-left">
              <span class="history-th-content">
                Created At
                <InsightsTooltip
                  :tooltip="sliderHeaderCreatedDateTooltip"
                  tooltip-direction="end"
                  class="ml-1"
                  @click.stop
                />
              </span>
            </th>
            <th class="text-left">
              <span class="history-th-content">
                Status
                <InsightsTooltip
                  :tooltip="sliderHeaderStatusTooltip"
                  tooltip-direction="end"
                  class="ml-1"
                  @click.stop
                />
              </span>
            </th>
            <th
              class="text-right"
              style="width: 48px"
            />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ack in acknowledgements"
            :key="ack.ulid"
          >
            <td>
              <div class="history-job-profile-cell">
                <div class="text-body-2 text-textDark">
                  {{ ack.title }}
                </div>
                <div
                  v-if="ack.hris_job_code"
                  class="text-body-3 text-textLight"
                >
                  {{ ack.hris_job_code }}
                </div>
              </div>
            </td>
            <td class="text-textDark">
              {{ formatDisplayDate(ack.created_at) }}
            </td>
            <td class="status-cell">
              <EmployeeAcknowledgementStatusPill
                :status-key="acknowledgementStatusKey(ack.status)"
                :label="acknowledgementStatusLabel(ack.status)"
              />
            </td>
            <td class="text-right">
              <v-menu
                v-if="ack.status === 'signed' || ack.status === 'pending'"
                class="history-ack-menu"
                content-class="history-ack-dropdown"
                location="bottom end"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <v-btn
                    v-bind="menuProps"
                    icon
                    variant="text"
                    size="small"
                    color="textDark"
                  >
                    <v-icon icon="$dots-vertical" />
                  </v-btn>
                </template>
                <v-list
                  density="compact"
                  class="history-ack-list"
                >
                  <template v-if="ack.status === 'signed'">
                    <v-list-item
                      :href="getEmployeeAcknowledgementViewPdfUrl(ack.ulid)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="history-ack-list-item text-body-2 text-textDark"
                    >
                      <template #prepend>
                        <v-icon
                          icon="$magnify"
                          size="small"
                        />
                      </template>
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      class="history-ack-list-item text-body-2 text-textDark"
                      @click="downloadAcknowledgementPdf(ack.ulid)"
                    >
                      <template #prepend>
                        <v-icon
                          icon="$tray-arrow-down"
                          size="small"
                        />
                      </template>
                      <v-list-item-title>Download</v-list-item-title>
                    </v-list-item>
                  </template>
                  <v-list-item
                    v-else-if="ack.status === 'pending'"
                    class="history-ack-list-item history-ack-list-item-cancel text-body-2"
                    @click="emit('cancel', ack)"
                  >
                    <template #prepend>
                      <v-icon
                        icon="$delete-outline"
                        size="small"
                        color="error"
                      />
                    </template>
                    <v-list-item-title>Cancel</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import EmployeeAcknowledgementHistoryEmptyState
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementHistoryEmptyState.vue';
import EmployeeAcknowledgementStatusPill from
  '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementStatusPill.vue';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { getTooltip } from '@/helpers/tooltips';
import { formatDisplayDate } from '@/helpers/date';
import {
  getEmployeeAcknowledgementViewPdfUrl,
  getEmployeeAcknowledgementDownloadPdfUrl,
} from '@/api/jobProfile';
import { clickToDownload } from '@/helpers/fileDownload';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  acknowledgementStatusKey,
  acknowledgementStatusLabel,
} from '@/components/library/employeeAcknowledgements/helpers';

const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

defineProps({
  acknowledgements: {
    type: Array,
    default: () => [],
  },
});

const sliderHeaderProfileTooltip = computed(() => getTooltip('acknowledgement-slider-header-profile'));
const sliderHeaderCreatedDateTooltip = computed(() => getTooltip('acknowledgement-slider-header-created_date'));
const sliderHeaderStatusTooltip = computed(() => getTooltip('acknowledgement-slider-header-status'));

const emit = defineEmits(['cancel']);

const downloadAcknowledgementPdf = (ulid) => {
  try {
    const pdfUrl = getEmployeeAcknowledgementDownloadPdfUrl(ulid);

    clickToDownload(pdfUrl);

    addSuccessSnackbar({ text: 'Your PDF is downloading.' });
  } catch (error) {
    addErrorSnackbar({
      text: error?.message || 'Failed to start PDF download.',
    });
  }
};

</script>

<style scoped lang="less">
.history-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px 16px 24px;
}

.history-table-wrap {
  overflow-x: auto;
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 5px;
  padding-right: 5px;
}

.history-table {
  width: 100%;
  border-collapse: collapse;

  th {
    vertical-align: middle;
  }

  tbody td {
    vertical-align: top;
  }

  th {
    padding: 18px 16px;
    font-weight: 600;
    color: @text-darkest;
    background-color: #f5f5f5;
    border-bottom: 1px solid @stroke-default;
  }

  .history-th-content {
    display: inline-flex;
    align-items: center;
  }

  td {
    padding: 12px;
    border-bottom: 1px solid @stroke-default;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .history-job-profile-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .status-cell :deep(.ack-status-pill) {
    min-width: 86px;
  }
}
</style>

<style lang="less">
.history-ack-dropdown,
.history-ack-dropdown .v-list {
  box-shadow: none !important;
}

.history-ack-dropdown {
  border-radius: 8px !important;
  border: 1px solid @stroke-default !important;
  padding: 12px !important;
  background-color: @white !important;

  .history-ack-list {
    padding: 0 !important;
  }

  .history-ack-list-item.v-list-item {
    min-height: 40px;
    padding-inline: 0;
    padding-block: 0;

    .v-list-item__content {
      padding: 0;
    }

    .v-list-item__overlay {
      background: transparent;
    }

    .v-list-item__prepend {
      min-width: 0;
      max-width: 20px;
      margin-inline-end: 6px;
    }

    .v-list-item__prepend .v-icon {
      font-size: 18px;
    }

    .v-list-item-title {
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
    }
  }

  .history-ack-list-item:hover {
    cursor: pointer;
  }

  .history-ack-list-item:hover .v-list-item__overlay {
    background: @bg-canvas;
  }

  .history-ack-list-item:hover .v-list-item-title,
  .history-ack-list-item:hover .v-list-item__prepend .v-icon {
    color: @text-brand;
  }

  .history-ack-list-item-cancel:hover .v-list-item-title,
  .history-ack-list-item-cancel:hover .v-list-item__prepend .v-icon {
    color: @text-error;
  }
}
</style>
